import { SubmitButton } from 'components/common/buttons/SubmitButton';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { fetchInterpreters } from 'slices/interpreterSlice';
import { selectCustomStyles } from './Select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { CheckinCheckout } from 'components/ScheduleForm';
import { CheckinDateTimeInputs } from 'components/ScheduleForm';
import { CheckoutDateTimeInputs } from 'components/ScheduleForm';
import { CommentsAndNotes } from 'components/ScheduleForm';

function SelectInterpreterModal({
    isOpen,
    onClose,
    onConfirm,
    item,
    loader,
    appointmentId,
    excludeInterpreterId,
}) {

    const dispatch = useDispatch();
    const methods = useForm();
    const { setFocus, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, clearErrors } = methods



    useEffect(() => {

        methods.reset({ cancel_reason: '' })

    }, [isOpen])

    useEffect(() => {
        // Route the assigned interpreter to the matching dropdown by
        // employment type (freelancers live in the Freelancer select).
        const assignedIsFreelancer = item?.interpreter?.employment_type === 'freelancer';
        const interpreterId = item?.appointment_assign?.interpreter_id;

        if (assignedIsFreelancer) {
            setValue("interpreter_id", "");
            setValue("freelancer_id", item?.interpreter?.id || "");
        } else if (interpreterId && interpreterId !== excludeInterpreterId) {
            setValue("interpreter_id", interpreterId);
            setValue("freelancer_id", "");
        } else {
            setValue("interpreter_id", "");
            setValue("freelancer_id", "");
        }

        setValue("checkin_date", item?.appointment_assign?.checkin_date || "");
        setValue("checkin_time", item?.appointment_assign?.checkin_time || "");
        setValue("checkout_date", item?.appointment_assign?.checkout_date || "");
        setValue("checkout_time", item?.appointment_assign?.checkout_time || "");
        setValue("comments", item?.appointment_assign?.comments || "Other");
        setValue("notes", item?.appointment_assign?.notes || "");
    }, [item, isOpen, excludeInterpreterId])

    const isFreelancer = useMemo(() => {
        return watch('freelancer_id') ? true : false
    }, [watch('freelancer_id'),isOpen])

    const isInterpreter = useMemo(() => {
        return watch('interpreter_id') ? true : false
    }, [watch('interpreter_id'),isOpen])

    // #region Freelancer dropdown (interpreters with employment_type = freelancer)
    const [filterFreelancer, setFilterFreelancers] = useState({
        label: "",
        value: ""
    })

    useEffect(() => {
        if (item?.interpreter?.employment_type === 'freelancer' && item?.interpreter?.id) {
            setFilterFreelancers({
                label: `${item?.interpreter?.first_name || ""} ${item?.interpreter?.last_name || ""}`.trim(),
                value: item?.interpreter?.id
            })
        } else {
            setFilterFreelancers(null)
        }
    }, [item?.interpreter])

    const filteredFreelancerLoadOptions = async (inputValue, loadedOptions, { page }) => {
        const result = await dispatch(
            fetchInterpreters(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue, employment_type: "freelancer", exclude_id: excludeInterpreterId }, true, true)
        );

        return {
            options: result.data.map(val => ({
                value: val.id,
                label: `${val.first_name || ""} ${val.last_name || ""}`.trim(),
            })),
            hasMore: page !== result?.last_page,
            additional: {
                page: page + 1,
            },
        };
    };
    //#endregion

    // #region Interpreter Dropdown (permanent staff only)
    const [filterInterpreters, setFilterInterpreters] = useState({
        label: "",
        value: ""
    })

    useEffect(() => {
        if (item?.interpreter?.employment_type !== 'freelancer' && item?.interpreter?.first_name && item?.interpreter?.id && item?.interpreter?.id !== excludeInterpreterId) {
            setFilterInterpreters({
                label: item?.interpreter?.first_name || "",
                value: item?.interpreter?.id || ""
            })
        } else {
            setFilterInterpreters(null)
        }
    }, [item?.interpreter, excludeInterpreterId])

    const filteredInterpretersLoadOptions = async (inputValue, loadedOptions, { page }) => {
        const result = await dispatch(fetchInterpreters(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue, employment_type: "permanent", exclude_id: excludeInterpreterId }, true, true));
        return {
            options: result.data.map(val => ({
                value: val.id,
                label: val.first_name + " " + val.last_name,
            })),

            hasMore: page !== result?.last_page,
            additional: {
                page: page + 1,
            },
        };
    };
    //#endregion


    return (
        <>
            <Modal isOpen={isOpen} toggle={onClose} centered className="modal-medium">
                <FormProvider {...methods}>
                    <form action="" onSubmit={handleSubmit((data) => {
                        // Freelancers are real interpreters — both selects submit as
                        // interpreter_id. vendor_id stays null (vendor assignment
                        // happens via the separate "Send to vendor" flow).
                        const payload = {
                            ...data,
                            interpreter_id: data.interpreter_id || data.freelancer_id || null,
                            vendor_id: null,
                        };
                        delete payload.freelancer_id;
                        onConfirm(payload, "assign");
                    })}>
                        <ModalHeader className='header_modal align-items-center'>
                            <h5 className="modal-title_u">{item?.status === 'assigned' ? 'Reassign' : 'Assign'}</h5>
                        </ModalHeader>
                        <ModalBody className='modal_form_body'>
                            <div className='step_cards step_1'>
                                <h2>{item?.status === 'assigned' ? 'Reassign' : 'Assign'}</h2>
                                

                                {/* INTERPRETER SELECT */}
                                <div className="row-primary">
                                    <h2>Interpreter</h2>
                                    <div className='w-100'>
                                        <Controller
                                            name="interpreter_id"
                                            control={control}
                                            render={({ field }) =>
                                                <div style={{ width: "100%" }}>
                                                    <AsyncPaginate
                                                        menuPlacement='auto'
                                                        styles={{
                                                            ...selectCustomStyles,
                                                        }}
                                                        isDisabled={isFreelancer}
                                                        value={filterInterpreters}
                                                        loadOptions={filteredInterpretersLoadOptions}
                                                        isClearable
                                                        escapeClearsValue
                                                        onChange={(obj) => {
                                                            if (obj?.value) {
                                                                setFilterInterpreters(obj)
                                                                field.onChange(obj.value);
                                                            }
                                                            else {
                                                                setFilterInterpreters(null)
                                                                field.onChange(null);
                                                            }
                                                        }}
                                                        additional={{
                                                            page: item?.state?.id ? 2 : 1,
                                                        }}
                                                    />
                                                </div>
                                            }
                                        />
                                    </div>
                                </div>

                                {/* FREELANCER SELECT (freelance interpreters) */}
                                <div className="row-primary">
                                    <h2>Freelancer</h2>
                                    <div className='w-100'>
                                        <Controller
                                            name="freelancer_id"
                                            control={control}
                                            render={({ field }) =>
                                                <div style={{ width: "100%" }}>
                                                    <AsyncPaginate
                                                        menuPlacement='auto'
                                                        styles={{
                                                            ...selectCustomStyles,
                                                        }}
                                                        isDisabled={isInterpreter} // Keep this for UI behavior
                                                        value={filterFreelancer}
                                                        loadOptions={filteredFreelancerLoadOptions}
                                                        isClearable
                                                        escapeClearsValue
                                                        onChange={(obj) => {
                                                            if (obj?.value) {
                                                                setFilterFreelancers(obj);
                                                                field.onChange(obj.value);
                                                            } else {
                                                                setFilterFreelancers(null);
                                                                field.onChange(null);
                                                            }
                                                        }}
                                                        additional={{
                                                            page: 1,
                                                        }}
                                                    />
                                                </div>
                                            }
                                        />
                                    </div>
                                </div>
                                {/* {
                                    watch("interpreter_id") &&
                                    <CheckinCheckout apptAssign={item?.appointment_assign} />
                                }
                                {
                                    ((watch("available_status") && watch("interpreter_id") && item?.appointment_assign?.interpreter_id)) && (
                                        <>
                                            <CheckinDateTimeInputs />
                                            <CheckoutDateTimeInputs />
                                            <CommentsAndNotes />
                                        </>
                                    )
                                } */}
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <SubmitButton btnText="Submit" loader={loader}/>
                            <button className="table_delete" type='button' onClick={onClose}>Cancel</button>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </Modal>
        </>
    );
}

export default SelectInterpreterModal;